import { inject, injectable } from "tsyringe";
import { TouchEvent } from 'react';
import { type IProjectBus, ProjectBusEvents, TOKEN_IProjectBus } from "@/services/ProjectBus";
import { TOKEN_IMouseState, type IMouseState } from "@/services/MouseState";
import { TOKEN_IProjectEventGenerator, type IProjectEventGenerator } from "@/services/ProjectEventGenerator";
import { ProjectAccessStore, TOKEN_ProjectAccessStore } from "./utility-stores/ProjectAccessStore";
import { TOKEN_ITransientLocator, type ITransientLocator } from "@/services/TransientLocator";
import { ColoredSquare } from "@/ProjectWorld";
import { EntityCollection } from "@utilitydelta/clientside-event-sourcing";
import { TOKEN_IProjectWorldReader, type IProjectWorldReader } from "@/services/ProjectWorldReader";
import { makeAutoObservable } from "mobx";
import SquareStore from "./transient/SquareStore";
import { numberToRgbaString } from "@/lib/utils";

/*
* Critical that this is a dense matrix so we don't force re-renders of the game space when squares are populated
*/

export const TOKEN_GameContainerStore = Symbol("GameContainerStore");

@injectable()
export class GameContainerStore {
    clearAllColor() {
        if (this._squareStores === undefined) return;
        const clearRgba = numberToRgbaString(0);
        this._squareStores.forEach(square => { square.rgbaString = clearRgba; })
    }

    private _squareStores: SquareStore[] | undefined;

    get squareStores(): SquareStore[] | undefined {
        return this._squareStores;
    }

    private _gridSize: number | undefined = 0;

    get gridSize(): number | undefined {
        return this._gridSize;
    }

    private _projectId: string | undefined;

    constructor(
        @inject(TOKEN_IProjectBus) readonly projectBus: IProjectBus,
        @inject(TOKEN_IMouseState) private readonly mouseState: IMouseState,
        @inject(TOKEN_IProjectEventGenerator) private readonly projectEventGenerator: IProjectEventGenerator,
        @inject(TOKEN_ITransientLocator) private readonly transientLocator: ITransientLocator,
        @inject(TOKEN_IProjectWorldReader) private readonly projectWorldReader: IProjectWorldReader,
        @inject(TOKEN_ProjectAccessStore) private readonly projectAccessStore: ProjectAccessStore

    ) {
        projectBus.subscribe_projectLoaded(this.onProjectLoaded);
        projectBus.subscribe_projectionUpdated(this.onProjectionUpdated);

        makeAutoObservable(this);
    }

    private onProjectionUpdated = async (data: ProjectBusEvents['PROJECTION_UPDATED']): Promise<void> => {
        if (!this._projectId || !this.gridSize || !this._squareStores) return;
        if (data.projectId !== this._projectId) return;
        if (data.mutationResult.kind !== 'SetColor_ProjectionResult') return;

        const index = data.mutationResult.square.x + data.mutationResult.square.y * this.gridSize;
        this._squareStores[index].rgbaString = numberToRgbaString(data.mutationResult.square.color);
    }

    public buildSquareStores = (gridSize: number, coloredSquares: EntityCollection<ColoredSquare>): SquareStore[] => {
        this._gridSize = gridSize;
        return Array.from({ length: gridSize * gridSize }, (_, index) => {
            const x = index % gridSize;
            const y = Math.floor(index / gridSize);
            return { x, y };
        }).map(dimensions => {
            const store = this.transientLocator.resolve<SquareStore>(SquareStore);
            store.init(dimensions.x, dimensions.y);
            const paintedSquare = coloredSquares.maybeGet(store.id);
            if (!paintedSquare) return store;

            store.rgbaString = numberToRgbaString(paintedSquare.color);
            return store;
        });
    }

    private onProjectLoaded = async (data: ProjectBusEvents['PROJECT_LOADED']): Promise<void> => {
        const projectWorld = await this.projectWorldReader.mustGet(data.projectId);
        if (this._projectId === data.projectId && this._gridSize === projectWorld.projectMetadata.gridSize) return;
        this._projectId = data.projectId;
        this._squareStores = this.buildSquareStores(projectWorld.projectMetadata.gridSize, projectWorld.coloredSquares);
    }

    handleMouseMove = (e: React.MouseEvent<HTMLDivElement> | undefined) => {
        if (!this._projectId || this.projectAccessStore.isUIReadOnly) return;
        if (!e) return;

        const performingAction = this.mouseState.isLeftButtonDown || this.mouseState.isRightButtonDown;
        if (!performingAction) return;

        const clientX = (e as React.MouseEvent<HTMLDivElement>).clientX;
        const clientY = (e as React.MouseEvent<HTMLDivElement>).clientY;
        const targetElement = document.elementFromPoint(clientX, clientY);

        if (targetElement && targetElement.classList.contains('color-square')) {
            const X = parseInt(targetElement.getAttribute('data-x') || '0', 10);
            const Y = parseInt(targetElement.getAttribute('data-y') || '0', 10);

            this.projectEventGenerator.setColor(this._projectId, X, Y, this.mouseState.isRightButtonDown);
        }
    }

    handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
        if (!this._projectId || this.projectAccessStore.isUIReadOnly) return;

        if (!this.mouseState.isTouchActive) return;

        for (let index = 0; index < e.touches.length; index++) {

            const touch = e.touches[index];
            const targetElement = document.elementFromPoint(touch.clientX, touch.clientY);

            const isDraggingOverSquare = targetElement != null && targetElement.classList.contains('color-square');
            if (!isDraggingOverSquare) continue;

            const X = parseInt(targetElement.getAttribute('data-x') || '0', 10);
            const Y = parseInt(targetElement.getAttribute('data-y') || '0', 10);

            this.projectEventGenerator.setColor(this._projectId, X, Y, this.mouseState.isRightButtonDown);
        }
    }
}